import { useQuery } from '@tanstack/react-query';
import api from '@/api';
import { useAuthStore } from '@/store/authStore';

interface FeatureFlag {
  id: string;
  name: string;
  display_name: string;
  description: string;
  is_enabled: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Hook to fetch and check feature flags for the current workspace.
 *
 * Note: The vigilantai (admin) workspace always has all features enabled.
 *
 * Usage:
 * ```tsx
 * const { isFeatureEnabled, isLoading } = useFeatureFlags();
 *
 * if (isFeatureEnabled('eval_studio')) {
 *   // Show Eval Studio tools
 * }
 * ```
 */
export function useFeatureFlags() {
  const { currentWorkspace } = useAuthStore();

  // The vigilantai workspace always has all features enabled
  const isVigilantWorkspace = currentWorkspace?.slug === 'vigilantai';

  const {
    data: featureFlags,
    isLoading,
    error,
  } = useQuery<FeatureFlag[]>({
    queryKey: ['feature-flags'],
    queryFn: async () => {
      const response = await api.get('/api/v1/feature-flags/');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    // Don't need to fetch for vigilantai - all features are always enabled
    enabled: !isVigilantWorkspace,
  });

  /**
   * Check if a feature is enabled by its name.
   * Returns true for vigilantai workspace (all features always enabled).
   * Returns false if the feature doesn't exist or flags are still loading.
   */
  const isFeatureEnabled = (featureName: string): boolean => {
    // Vigilantai workspace always has all features enabled
    if (isVigilantWorkspace) return true;

    if (!featureFlags) return false;
    const flag = featureFlags.find((f) => f.name === featureName);
    return flag?.is_enabled ?? false;
  };

  /**
   * Get a specific feature flag by name.
   */
  const getFeatureFlag = (featureName: string): FeatureFlag | undefined => {
    return featureFlags?.find((f) => f.name === featureName);
  };

  return {
    featureFlags,
    isLoading: isVigilantWorkspace ? false : isLoading,
    error,
    isFeatureEnabled,
    getFeatureFlag,
    isVigilantWorkspace,
  };
}

// Feature flag name constants for type-safety
// Add new flags here as they are created via migrations
export const FEATURE_FLAGS = {
  AI_CONFIGURATION: 'ai_configuration',
  OPTIMISATION_REVIEWS: 'optimisation_reviews',
  EVAL_STUDIO: 'eval_studio',
  QUALITY_BASELINES: 'quality_baselines',
  TEAMS_INTEGRATION: 'teams_integration',
  COST_ANALYTICS: 'cost_analytics',
  MANAGER_WORKER_ORCHESTRATION: 'manager_worker_orchestration',
  SKILLS_RUNTIME: 'skills_runtime',
  COMPUTER_USE: 'computer_use',
} as const;

export type FeatureFlagName = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];
